import React, { useState, useEffect, useRef } from 'react';
import { QUIZ_QUESTIONS, QUIZ_CHAPTERS } from '../../constants';
import Button, { ButtonType } from '@/components/Button';
import Text from '@/components/Text';
import { twMerge } from 'tailwind-merge';
import QuizCheckpoint from '../QuizCheckpoint';
import GaugeSlider from '../GaugeSlider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDiamond as faDiamondSolid,
  faStar as faStarSolid,
  faFire as faFireSolid,
  faCompass as faCompassSolid,
  faBolt as faBoltSolid
} from '@fortawesome/pro-solid-svg-icons';
import { faCircle as faCircleRegular } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface YourTypeQuizProps {
  onComplete: (answers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>) => void;
  onExit?: (payload: {
    questionIndex: number;
    questionId?: number;
    chapterTitle?: string;
    answeredCount: number;
    progressPercent: number;
    totalQuestions: number;
    stage: 'question' | 'checkpoint';
    exitTrigger: 'unmount' | 'pagehide' | 'beforeunload';
  }) => void;
}

const CHAPTER_STONES = [
  { icon: faDiamondSolid, color: 'text-emerald-400', glow: 'shadow-emerald-500/50', label: 'Decision' },
  { icon: faFireSolid, color: 'text-rose-400', glow: 'shadow-rose-500/50', label: 'Risk' },
  { icon: faCompassSolid, color: 'text-sky-400', glow: 'shadow-sky-500/50', label: 'Research' },
  { icon: faBoltSolid, color: 'text-violet-400', glow: 'shadow-violet-500/50', label: 'Management' },
  { icon: faStarSolid, color: 'text-amber-400', glow: 'shadow-amber-500/50', label: 'Values' },
];

export default function YourTypeQuiz({ onComplete, onExit }: YourTypeQuizProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>>({});
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [collectingStone, setCollectingStone] = useState<number | null>(null);
  const [isExitingStone, setIsExitingStone] = useState(false);
  const [isFusing, setIsFusing] = useState(false);
  const [tempSliderValue, setTempSliderValue] = useState<'A' | 'B' | 'C' | 'D' | 'E'>('A');
  const hasCompletedRef = useRef(false);
  const hasTrackedExitRef = useRef(false);
  const canTrackExitRef = useRef(false);
  const currentStepRef = useRef(0);
  const answersRef = useRef<Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>>({});
  const showCheckpointRef = useRef(false);

  const currentQuestion = QUIZ_QUESTIONS[currentStep];
  const nextQuestion = QUIZ_QUESTIONS[currentStep + 1];
  const chapterTitles = Array.from(new Set(QUIZ_QUESTIONS.map(q => q.partTitle)));
  const currentChapter = chapterTitles.indexOf(currentQuestion.partTitle);
  const isEndOfChapter = !nextQuestion || currentQuestion.partTitle !== nextQuestion.partTitle;

  const progress = ((currentStep + 1) / QUIZ_QUESTIONS.length) * 100;

  useEffect(() => {
    // Reset temp slider value when question changes
    if (currentQuestion?.type === 'slider') {
      setTempSliderValue(answers[currentQuestion.id] || 'A');
    }
  }, [currentStep, currentQuestion]);

  useEffect(() => {
    currentStepRef.current = currentStep;
    answersRef.current = answers;
  }, [currentStep, answers]);

  useEffect(() => {
    showCheckpointRef.current = showCheckpoint;
  }, [showCheckpoint]);

  const emitExit = (exitTrigger: 'unmount' | 'pagehide' | 'beforeunload') => {
    if (hasCompletedRef.current || hasTrackedExitRef.current || !canTrackExitRef.current) return;
    const step = currentStepRef.current;
    const current = QUIZ_QUESTIONS[step];
    const answeredCount = Object.keys(answersRef.current).length;
    onExit?.({
      questionIndex: step + 1,
      questionId: current?.id,
      chapterTitle: current?.partTitle,
      answeredCount,
      progressPercent: Math.round(((step + 1) / QUIZ_QUESTIONS.length) * 100),
      totalQuestions: QUIZ_QUESTIONS.length,
      stage: showCheckpointRef.current ? 'checkpoint' : 'question',
      exitTrigger,
    });
    hasTrackedExitRef.current = true;
  };

  useEffect(() => {
    // Avoid false-positive exit events from React Strict Mode's dev-only mount/unmount cycle.
    const armTimeout = window.setTimeout(() => {
      canTrackExitRef.current = true;
    }, 0);
    return () => window.clearTimeout(armTimeout);
  }, []);

  useEffect(() => {
    const handlePageHide = () => emitExit('pagehide');
    const handleBeforeUnload = () => emitExit('beforeunload');
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      emitExit('unmount');
    };
  }, [onExit]);

  const completeQuiz = (finalAnswers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'>) => {
    hasCompletedRef.current = true;
    onComplete(finalAnswers);
  };

  const getDominantValueForChapter = (stepIndex: number) => {
    const chapterTitle = QUIZ_QUESTIONS[stepIndex].partTitle;
    const chapterAnswers = Object.entries(answers)
      .filter(([id]) => {
        const q = QUIZ_QUESTIONS.find(q => q.id === parseInt(id));
        return q?.partTitle === chapterTitle && q?.type !== 'slider'; // Exclude sliders from roasts
      })
      .map(([_, val]) => val);

    const counts = chapterAnswers.reduce((acc, val) => {
      acc[val] = (acc[val] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return (Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'A') as 'A' | 'B' | 'C' | 'D';
  };

  const handleSelect = (value: 'A' | 'B' | 'C' | 'D' | 'E') => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);

    if (isEndOfChapter) {
      setCollectingStone(currentChapter);
      setTimeout(() => {
        setIsExitingStone(true);
        setTimeout(() => {
          if (currentStep < QUIZ_QUESTIONS.length - 1) {
            setShowCheckpoint(true);
          } else {
            // Final Chapter Fusion Sequence
            setIsFusing(true);
            setTimeout(() => {
              completeQuiz(newAnswers);
            }, 2600);
          }
          setCollectingStone(null);
          setIsExitingStone(false);
        }, 600); // Match animate-stone-collect-exit duration
      }, 1500); // Keep stone visible slightly longer for impact
    } else if (currentStep < QUIZ_QUESTIONS.length - 1) {
      // Auto-advance for choices, give more time for slider confirm
      const delay = currentQuestion.type === 'slider' ? 0 : 300;
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, delay);
    } else {
      completeQuiz(newAnswers);
    }
  };

  const handleContinue = () => {
    setShowCheckpoint(false);
    setCurrentStep(currentStep + 1);
  };

  return (
    <div className="flex flex-col gap-4 md:gap-6 py-2 md:py-6 md:px-4 max-w-xl mx-auto min-h-[500px] md:min-h-[600px] w-full relative">
      {/* Stone Tray */}
      <div className={twMerge(
        "flex justify-center gap-1 md:gap-4 mb-2 transition-all duration-1000",
        isFusing && "opacity-0 scale-150 blur-xl"
      )}>
        {CHAPTER_STONES.map((stone, idx) => {
          const isCollected = idx < currentChapter || (idx === currentChapter && isEndOfChapter && answers[currentQuestion.id]);
          const isCurrent = idx === currentChapter;
          const isCollecting = collectingStone === idx;

          return (
            <div key={idx} className="flex flex-col items-center gap-1 md:gap-2 w-12 md:w-20 sm:w-24">
              <div
                className={twMerge(
                  "size-8 md:size-10 rounded-xl flex items-center justify-center transition-all duration-700 relative group",
                  isCollected ? twMerge("shadow-lg bg-white/10", stone.glow) : "bg-white/5 grayscale opacity-30",
                  isCurrent && !isCollected && "grayscale-0 opacity-100 border border-white/20 animate-pulse",
                  isCollecting && "scale-150 z-20 brightness-150 rotate-[360deg]"
                )}
              >
                {/* Colored background for collected stones */}
                {isCollected && (
                  <div className={twMerge(
                    "absolute inset-0 rounded-xl opacity-20",
                    stone.color.replace('text-', 'bg-')
                  )} />
                )}

                {isCollected ? (
                  <FontAwesomeIcon
                    icon={stone.icon as IconProp}
                    className={twMerge("size-3.5 md:size-5", stone.color, isCollecting && "animate-bounce")}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faCircleRegular as IconProp}
                    className="size-3 md:size-4 text-white/40"
                  />
                )}

                {/* Glow Effect */}
                {isCollected && (
                  <div className={twMerge(
                    "absolute inset-0 rounded-xl blur-md -z-10 opacity-50",
                    stone.color.replace('text-', 'bg-')
                  )} />
                )}
              </div>
              {/* @ts-ignore */}
              <Text size="[10px]" className={twMerge(
                "uppercase tracking-widest font-bold transition-opacity duration-500 hidden md:block",
                isCurrent ? "opacity-100 text-white/60" : "opacity-0"
              )}>
                {stone.label}
              </Text>
            </div>
          );
        })}
      </div>

      {showCheckpoint ? (
        /* @ts-ignore */
        <QuizCheckpoint
          chapterId={currentChapter + 1}
          dominantValue={getDominantValueForChapter(currentStep)}
          onContinue={handleContinue}
        />
      ) : (
        <div className={twMerge(
          "flex flex-col gap-6 w-full transition-all duration-500",
          (collectingStone !== null || isFusing) ? "opacity-0 scale-95" : "opacity-100 scale-100"
        )}>
          {/* Progress Bar - Minimalist */}
          <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
            <div
              className="bg-teal-200 h-full transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              {/* @ts-ignore */}
              <Text size="xs" className="text-teal-200/60 uppercase tracking-[0.2em] font-bold text-[10px] md:text-xs">
                {currentQuestion.partTitle}
              </Text>
              {/* @ts-ignore */}
              <Text size="xs" className="text-white/20 font-mono text-[10px] md:text-xs">
                Q{currentStep + 1}
              </Text>
            </div>
            <h2
              className="text-lg md:text-lg font-sans font-bold leading-tight text-white"
              dangerouslySetInnerHTML={{ __html: currentQuestion.text }}
            />
          </div>

          {currentQuestion.type === 'slider' ? (
            <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <GaugeSlider
                value={tempSliderValue}
                onChange={(val) => setTempSliderValue(val as 'A' | 'B' | 'C' | 'D' | 'E')}
                theme={currentQuestion.id === 17 ? 'time' : 'risk'}
                options={currentQuestion.options.map(opt => ({
                  label: opt.label,
                  value: opt.value,
                  text: opt.text,
                  description: opt.description || ''
                }))}
              />
              <button
                onClick={() => handleSelect(tempSliderValue)}
                className={twMerge(
                  "relative w-full py-4 md:py-6 rounded-full font-black text-xs md:text-xs uppercase tracking-[0.3em] transition-all duration-500",
                  "bg-white text-black hover:bg-teal-200 hover:shadow-[0_0_40px_rgba(153,246,228,0.3)] hover:-translate-y-1 active:scale-95 z-20 flex items-center justify-center gap-4 overflow-hidden group/btn"
                )}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-[shimmer_2s_infinite]" />
                <span className="relative z-10">Lock it in</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 md:gap-3">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  disabled={collectingStone !== null || isFusing}
                  className={twMerge(
                    "flex items-center gap-3 md:gap-4 p-4 md:p-6 rounded-2xl md:rounded-[2rem] border border-white/5 bg-white/5 transition-all duration-300 text-start group relative overflow-hidden",
                    "hover:bg-white/10 hover:border-white/20 hover:-translate-y-0.5",
                    answers[currentQuestion.id] === option.value && "border-teal-200/50 bg-teal-200/5 shadow-[0_0_30px_rgba(153,246,228,0.05)]"
                  )}
                >
                  <span
                    className={twMerge(
                      "text-md md:text-lg transition-colors duration-300",
                      answers[currentQuestion.id] === option.value ? "text-white font-bold" : "text-white/70 group-hover:text-white"
                    )}
                    dangerouslySetInnerHTML={{ __html: option.text }}
                  />


                  {/* Selection Glow */}
                  {answers[currentQuestion.id] === option.value && (
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-200/10 to-transparent -z-10 animate-in fade-in slide-in-from-left-full duration-1000" />
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="mt-auto flex justify-between items-center pt-6">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0 || collectingStone !== null || isFusing}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/30 hover:text-white/100 transition-colors disabled:opacity-0"
            >
              ← Previous
            </button>
            {/* @ts-ignore */}
            <Text size="xs" className="text-white/10 uppercase tracking-widest font-bold">
              {Math.floor(progress)}% Complete
            </Text>
          </div>
        </div>
      )}
      {/* Stone Collection Animation Overlay */}
      {collectingStone !== null && !isFusing && (
        <div className={twMerge(
          "absolute inset-0 flex flex-col items-center justify-center z-50",
          isExitingStone ? "animate-stone-collect-exit" : "animate-in fade-in zoom-in duration-500"
        )}>
          <div className={twMerge(
            "size-24 md:size-32 rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl animate-bounce",
            CHAPTER_STONES[collectingStone].glow
          )}>
            <FontAwesomeIcon
              icon={CHAPTER_STONES[collectingStone].icon as IconProp}
              className={twMerge("size-12 md:size-16 animate-pulse", CHAPTER_STONES[collectingStone].color)}
            />
          </div>
          {/* @ts-ignore */}
          <Text size="xl" className="mt-6 md:mt-8 font-sans font-bold text-white uppercase tracking-[0.3em] animate-pulse text-lg md:text-xl text-center px-4">
            {CHAPTER_STONES[collectingStone].label} Stone Collected
          </Text>
        </div>
      )}

      {/* Fusion Animation Overlay */}
      {isFusing && (
        <div className="absolute inset-0 flex items-center justify-center z-[100] bg-black/40 backdrop-blur-sm overflow-hidden rounded-[2rem] md:rounded-[3rem]">
          {/* Fusion Center */}
          <div className="relative flex items-center justify-center">
            {/* Swirling Stones */}
            {CHAPTER_STONES.map((stone, idx) => (
              <div
                key={idx}
                className="absolute animate-stone-spiral"
                style={{ animationDelay: `${idx * 0.15}s` }}
              >
                <div className={twMerge(
                  "size-8 md:size-12 rounded-xl md:rounded-2xl flex items-center justify-center bg-white/10 border border-white/20 shadow-2xl",
                  stone.glow
                )}>
                  <FontAwesomeIcon icon={stone.icon as IconProp} className={twMerge("size-4 md:size-6", stone.color)} />
                </div>
              </div>
            ))}

            {/* Central Fusion Glow */}
            <div className="size-16 md:size-24 rounded-full bg-white blur-[40px] md:blur-[50px] animate-fusion-glow opacity-50" />
            <div className="absolute size-32 md:size-48 rounded-full bg-teal-400 blur-[60px] md:blur-[80px] animate-pulse opacity-20" />

            {/* @ts-ignore */}
            <Text size="lg" className="absolute top-20 md:top-24 left-1/2 -translate-x-1/2 whitespace-nowrap font-sans font-black text-white uppercase tracking-[0.5em] animate-pulse text-md md:text-lg">
              Fusing Insights
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

